// hooks/iframe/usePersistedChat.js
import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const usePersistedChat = (agentId, apiKey) => {
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  
  // Gera chave única para o localStorage baseada no agentId
  const getStorageKey = useCallback(() => {
    return `agent_chat_${agentId}`;
  }, [agentId]);

  // Salva chatId no localStorage
  const saveChatId = useCallback((chatId) => {
    if (!chatId) return;
    
    const storageKey = getStorageKey();
    const chatData = {
      chatId,
      agentId,
      lastUpdated: new Date().toISOString()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(chatData));
    console.log('Chat ID salvo no localStorage:', chatId);
  }, [agentId, getStorageKey]);

  // Recupera chatId do localStorage
  const getPersistedChatId = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return null;
      
      const chatData = JSON.parse(stored);
      
      // Valida se é do mesmo agente
      if (chatData.agentId !== agentId) return null;
      
      // Opção: verificar se não é muito antigo (ex: 7 dias)
      const lastUpdated = new Date(chatData.lastUpdated);
      const daysSinceUpdate = (new Date() - lastUpdated) / (1000 * 60 * 60 * 24);
      
      if (daysSinceUpdate > 7) {
        localStorage.removeItem(storageKey);
        return null;
      }
      
      return chatData.chatId;
    } catch (error) {
      console.error('Erro ao recuperar chat do localStorage:', error);
      return null;
    }
  }, [agentId, getStorageKey]);

  // Busca dados do chat pela API
  const fetchChatData = useCallback(async (chatId) => {
    try {
      setLoadingChat(true);
      setChatError(null);

      let headers = {
          'Content-Type': 'application/json',
        }

        if (apiKey.startsWith("pawsf")) {
          headers['X-API-KEY'] = apiKey
        } else {
          headers['Authorization'] = `Bearer ${apiKey}`
        }
      
      const response = await fetch(`${API_BASE_URL}/chats/${chatId}`, {
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 404) {
          const storageKey = getStorageKey();
          localStorage.removeItem(storageKey);
          return null;
        }
        throw new Error(`Erro ao buscar chat: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar dados do chat:', error);
      setChatError(error.message);
      return null;
    } finally {
      setLoadingChat(false);
    }
  }, [apiKey, getStorageKey]);

  // Busca mensagens do chat
  const fetchChatMessages = useCallback(async (chatId) => {
    try {

      let headers = {
          'Content-Type': 'application/json',
        }

        if (apiKey.startsWith("pawsf")) {
          headers['X-API-KEY'] = apiKey
        } else {
          headers['Authorization'] = `Bearer ${apiKey}`
        }

      const response = await fetch(`${API_BASE_URL}/chats/${chatId}/messages?limit=100`, {
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar mensagens: ${response.status}`);
      }

      const data = await response.json();
      return data.messages || [];
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
      return [];
    }
  }, [apiKey]);

  // Processa mensagens para aplicar quebra de blocos
  const processMessagesForDisplay = useCallback((messages) => {
    const processedMessages = [];
    
    messages.forEach((msg, index) => {
      const agentMessage = msg.agent_msg || msg;
      
      // Apenas processa mensagens do usuário e assistente
      if (agentMessage.role !== 'user' && agentMessage.role !== 'assistant') {
        return;
      }

      const messageContent = agentMessage.content || '';
      
      // Para mensagens do usuário, adiciona diretamente
      if (agentMessage.role === 'user') {
        processedMessages.push({
          id: `msg_${index}_${Date.now()}`,
          type: 'user',
          text: messageContent,
          timestamp: new Date(msg.created_at || Date.now())
        });
        return;
      }

      // Para mensagens do assistente, aplica quebra de blocos
      if (agentMessage.role === 'assistant') {
        const blocks = breakMessageIntoBlocks(messageContent);
        
        blocks.forEach((block, blockIndex) => {
          if (block.trim().length > 0) {
            processedMessages.push({
              id: `msg_${index}_block_${blockIndex}_${Date.now()}`,
              type: 'assistant',
              text: block.trim(),
              timestamp: new Date(msg.created_at || Date.now())
            });
          }
        });
      }
    });

    return processedMessages;
  }, []);

  // Função para quebrar mensagens em blocos (baseada no useChatStream)
  const breakMessageIntoBlocks = useCallback((text) => {
    if (!text || text.trim().length < 10) {
      return [text];
    }

    const blocks = [];
    let currentBlock = '';
    let i = 0;

    while (i < text.length) {
      currentBlock += text[i];
      
      // Verificar se encontrou um separador
      const separators = ['.', '?', '!', '\n'];
      if (separators.includes(text[i])) {
        // Verificar se não está quebrando markdown ou URL
        const remainingText = text.substring(i + 1);
        
        // Verificações de markdown de imagem
        const isMarkdownImageStart = 
          currentBlock.endsWith('!') && remainingText.trim().startsWith('[');
        
        const hasUnfinishedMarkdownImage = 
          /\!\[[^\]]*$/.test(currentBlock) || 
          /\!\[[^\]]*\]\([^\)]*$/.test(currentBlock);
        
        // Verificações de URL
        const urlLikePattern = /(https?:\/\/[^\s]*|www\.[^\s]*)$/;
        const isLikelyUrlStart = urlLikePattern.test(currentBlock);
        
        const isUrlIncomplete = 
          isLikelyUrlStart ||
          /\.[a-zA-Z]{2,}$/.test(currentBlock) ||
          (currentBlock.endsWith('?') && remainingText.match(/^[a-zA-Z0-9=&]/));
        
        // Se não está quebrando estruturas importantes e tem tamanho mínimo
        if (!isMarkdownImageStart && !hasUnfinishedMarkdownImage && !isUrlIncomplete) {
          if (currentBlock.trim().length >= 10) {
            blocks.push(currentBlock.trim());
            currentBlock = '';
          }
        }
      }
      
      i++;
    }

    // Adicionar qualquer texto restante
    if (currentBlock.trim().length > 0) {
      blocks.push(currentBlock.trim());
    }

    return blocks.length > 0 ? blocks : [text];
  }, []);

  // Limpa o chat persistido
  const clearPersistedChat = useCallback(() => {
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    console.log('Chat removido do localStorage');
  }, [getStorageKey]);

  return {
    saveChatId,
    getPersistedChatId,
    fetchChatData,
    fetchChatMessages,
    processMessagesForDisplay,
    clearPersistedChat,
    loadingChat,
    chatError
  };
};